import { ITimezone } from '../../interfaces/timezone';
import { Autocomplete, TextField, Typography } from '@mui/material';
import { TEXTS } from '../../core/texts';
import { Validator } from '../../core/Validator';
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useGetTimezones } from '../../queryHooks/useGetTimezones';
import { ValidationErrorState } from '../../types/validationErrorState';

interface IProps {
  value: string;
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void,
  timezoneNamespace?: string,
  disabled?: boolean,
  variant?: 'standard' | 'outlined' | 'filled',
  validationErrors?: ValidationErrorState,
  prePopulateClientTimezone?: boolean
}

const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const TimezoneAutocomplete = ({
                                       value,
                                       handleInputChange,
                                       timezoneNamespace = 'timezone',
                                       disabled,
                                       variant,
                                       validationErrors,
                                       prePopulateClientTimezone = true
                                     }: IProps) => {
  const { data: list, isFetching } = useGetTimezones();
  const zonesList = useMemo(() => list || [], [list]);
  const selectedTimezone = useMemo(() => (list || []).find(tz => tz.id === value), [list, value]);

  const defaultTimeZone = useMemo(() =>
    prePopulateClientTimezone ? list?.find(zone => zone.displayName.toLowerCase().includes(clientTimezone.toLowerCase())) || null : null,
    [list, prePopulateClientTimezone]);

  const triggerSyntheticEvent = useCallback((timezone?: string) => {
    handleInputChange({
      target: {
        name: 'timezone',
        value: timezone || '',
        type: 'text'
      }
    } as unknown as ChangeEvent<HTMLInputElement>);
  }, [handleInputChange]);

  useEffect(() => {
    if (!value && defaultTimeZone) {
      triggerSyntheticEvent(defaultTimeZone.id);
    }
  }, [value, defaultTimeZone, triggerSyntheticEvent]);

  const handleTimezoneChange = useCallback((e: SyntheticEvent<Element, Event>) => {
    if ((e as any)?.code !== 'Enter') {
      const name = e.currentTarget.textContent?.trim();
      const selectedTimezone = zonesList.find(c => c.displayName === name);
      triggerSyntheticEvent(selectedTimezone?.id);
    }
  }, [zonesList, triggerSyntheticEvent]);

  const handleTimezonePressEnter = useCallback((e: SyntheticEvent<Element, Event>, val: string) => {
    if ((e as any)?.code === 'Enter') {
      const selectedTimezone = zonesList.find(c => c.displayName === val.trim());
      handleInputChange({
        target: {
          name: 'timezone',
          value: selectedTimezone?.id || ''
        }
      } as unknown as ChangeEvent<HTMLInputElement>);
    }
  }, [handleInputChange, zonesList]);

  return (
    <Autocomplete
      options={zonesList}
      getOptionLabel={(option: ITimezone) => option.displayName}
      loading={isFetching}
      blurOnSelect={true}
      onInputChange={handleTimezonePressEnter}
      onChange={handleTimezoneChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={selectedTimezone || defaultTimeZone}
      disabled={disabled}
      sx={{ width: '100%', minWidth: 280 }}
      renderOption={(props, option) => (
        <Typography {...props}> {option.displayName} </Typography>
      )}
      renderInput={(params) => (
        <TextField {...params}
                   label={TEXTS.TIMEZONE}
                   inputProps={{ ...params.inputProps, autoComplete: 'none' }}
                   error={validationErrors && !!validationErrors[timezoneNamespace]}
                   helperText={validationErrors && validationErrors[timezoneNamespace]}
                   required={validationErrors && !!Validator.LENGTHS.AVAILABILITY[timezoneNamespace]?.min}
                   variant={variant}
        />
      )}
    />
  );
};